/**
 * Letter and EED-deadline filters, one dropdown per group.
 *
 * Both are toggles over the collection already loaded, not new requests — see
 * lib/filter.ts for why the rule is duplicated from the backend.
 *
 * They used to sit in a band of their own between the top bar and the map. On a
 * laptop that band cost ~2.8rem of map for controls touched once per
 * municipality and then never again, so each group now lives behind its own
 * trigger in the top bar. Collapsing them hides *which* filters are on, which is
 * why every trigger states its group's current selection: a closed menu with no
 * such signal is how a user ends up staring at two thirds of a municipality with
 * nothing on screen explaining where the rest went.
 *
 * The dropdown itself is Dropdown.tsx, shared with the layer controls.
 */

import { useEffect } from "react";

import { Dropdown, useCloseMenus } from "./Dropdown";
import type { BuildingFilters } from "./lib/filter";
import {
  DEADLINES, DEADLINE_LABELS, LETTERS, LETTER_COLORS, LETTER_TEXT,
} from "./lib/letters";

interface Props {
  filters: BuildingFilters;
  onChange: (next: BuildingFilters) => void;
  disabled?: boolean;
}

/** The letters group, as its trigger states it. */
function lettersValue(letters: ReadonlySet<string>): string {
  if (letters.size === LETTERS.length) return "Todas";
  if (letters.size === 0) return "Ninguna";
  const on = LETTERS.filter((letter) => letters.has(letter));
  // Past three the list is longer than the trigger and stops being readable at
  // a glance; the count still says the map is showing a subset.
  return on.length <= 3 ? on.join(", ") : `${on.length} letras`;
}

export function FilterBar({ filters, onChange, disabled }: Props) {
  const closeMenus = useCloseMenus();

  const lettersActive = filters.letters.size !== LETTERS.length;
  const deadlineActive = filters.deadline !== null;

  // Switching municipality clears the GeoJSON, and a panel left open over the
  // load would be a set of controls that no longer do anything.
  useEffect(() => { if (disabled) closeMenus(); }, [disabled, closeMenus]);

  function toggleLetter(letter: string) {
    const letters = new Set(filters.letters);
    if (letters.has(letter)) letters.delete(letter);
    else letters.add(letter);
    onChange({ ...filters, letters });
  }

  function toggleDeadline(year: number) {
    // Clicking the active one clears it. A deadline filter with no way back to
    // "all" is a trap: the letters are still lit, so nothing on screen explains
    // why two thirds of the map vanished.
    onChange({ ...filters, deadline: filters.deadline === year ? null : year });
  }

  return (
    <>
      <Dropdown
        id="letters"
        label="Letra"
        value={lettersValue(filters.letters)}
        active={lettersActive}
        disabled={disabled}
        onReset={() => onChange({ ...filters, letters: new Set(LETTERS) })}
      >
        <div className="letter-filter">
          {LETTERS.map((letter) => {
            const on = filters.letters.has(letter);
            return (
              <button
                key={letter}
                type="button"
                className={on ? "letter-btn active" : "letter-btn"}
                // aria-pressed, not just a class: the on/off state is the entire
                // meaning of these buttons, and colour alone does not carry it.
                aria-pressed={on}
                aria-label={`Letra ${letter}`}
                style={{
                  background: LETTER_COLORS[letter],
                  color: LETTER_TEXT[letter],
                }}
                onClick={() => toggleLetter(letter)}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </Dropdown>

      <Dropdown
        id="deadline"
        label="Obligación EED"
        value={filters.deadline === null ? "Todas" : `Antes ${filters.deadline}`}
        active={deadlineActive}
        disabled={disabled}
        onReset={() => onChange({ ...filters, deadline: null })}
      >
        <div className="deadline-filter">
          {DEADLINES.map((year) => (
            <button
              key={year}
              type="button"
              className={
                filters.deadline === year ? "deadline-btn active" : "deadline-btn"
              }
              aria-pressed={filters.deadline === year}
              onClick={() => toggleDeadline(year)}
            >
              Antes {year} ({DEADLINE_LABELS[year]})
            </button>
          ))}
        </div>
      </Dropdown>

      {/* Only once there is something to clear. A permanently disabled button
          sits in a bar that is already competing with the search box and the
          status for width. */}
      {(lettersActive || deadlineActive) && (
        <button
          type="button"
          className="reset-btn"
          // Shortened on screen; the accessible name still contains the visible
          // text, so voice control can act on what it reads (WCAG 2.5.3).
          aria-label="Limpiar filtros"
          onClick={() => onChange({ letters: new Set(LETTERS), deadline: null })}
        >
          Limpiar
        </button>
      )}
    </>
  );
}
